import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
  Modal,
  Icon,
  Menu,
  MenuItem,
} from "@mui/material";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import FilterChips from "../InventoryComponents/FilterChips"; // Import FilterChips component
import UpdateLadderStatusModal from './UpdateLadderStatusModal';
import EditBondLadder from '../BondLadderComponents/EditBondLadder';
import { useAuth } from "../../Context/AuthProvider";
import { updateBondLadderStatus } from "../../http/api";
import { useNavigate } from "react-router-dom";
import { handleViewInventory } from "../../Utils/viewInventoryHandler";
import { useBondLadderRungFlowStore } from "../../Store/bondLadderRungFlowStore";
import { useActualOrderStore } from "../../Store/actualOrderStore";
import { useBondCharacteristicStore } from "../../Store/bondCharacteristicStore";
import { BorderColor, Update, UpdateTwoTone } from "@mui/icons-material";
import { processBondData } from "../OrderComponents/utils";
import { tokens } from '../../Common/designTokens'; // added token import

const BOND_LADDER_DETAIL_ROUTE = "/bond-ladder-detail";

const BondLadderHeader = ({
  input,
  totalElements,
  loading,
  lastUpdatedAt,
  selectedRung,
  totalRungs,
  onCustomizeClick,
  onDownloadCSV,
  csvLoading,
  customizeButtonRef,
  onDeleteChip,
  onClearFilters,
  ladderData,
  isRungExecuted = false, // <-- new prop
}) => {
  const [viewAllModalOpen, setViewAllModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth ? auth.token : null; // fallback if token is not available
  // State for modal
  const [updateLadderModalOpen, setUpdateLadderModalOpen] = useState(false);
  const [updateError, setUpdateError] = useState(''); // Add error state
  const setLadderData = useBondLadderRungFlowStore(state => state.setLadderData);
  const setCurrentCharacteristicID = useBondCharacteristicStore(state => state.setCurrentCharacteristicID);
  const setActualOrderData = useActualOrderStore(state => state.setActualOrderData);

  // Handler for updating ladder status
  const handleUpdateLadderStatus = async (comment) => {
    try {
      await updateBondLadderStatus(token, ladderData?.bondLadderId, comment);
      // Optionally show a success message or refresh data
      navigate(`/order?view=0&tab=2`);
      setUpdateLadderModalOpen(false);
    } catch (err) {
      // Pass error to modal instead of logging
      throw new Error(err.message || "Failed to update ladder status");
    }
  };

  // Handler for Edit button
  const handleEditClick = () => {
    console.log("DEBUG LADDER DATA:", ladderData);
    setEditModalOpen(true);
  };

  const handleEditSubmit = (updatedData) => {
    console.log("DEBUG UPDATED LADDER DATA:", updatedData);
    setEditModalOpen(false);
    // Simulate view inventory click with new data if on BondLadderDetails page
    if (window.location.pathname.startsWith(BOND_LADDER_DETAIL_ROUTE)) {
      //preserve account,side and pm in the updated data
      // updatedData.pm = ladderData.pm; 
      updatedData.userName = ladderData.userName;
      updatedData.account = ladderData.account;
      const updatedProcessBondData = processBondData(updatedData);
      setLadderData(updatedProcessBondData);
      handleViewInventory(
        2,
        updatedProcessBondData,
        setCurrentCharacteristicID,
        setActualOrderData,
        navigate,
        (error) => {
          console.error("Error navigating to ladder inventory:", error);
          alert(error);
        },
        () => {} // no loading spinner needed here
      );
    }
  };

  // Format value function
  const formatValue = (key, value) => {
    if (key.includes('Maturity') || key === 'nextCallDate') {
      return dayjs(value).format('YYYY-MM-DD');
    }
    if (['zeroCoupon', 'insured', 'altMinTax', 'taxable', 'callable'].includes(key)) {
      return value === '1' || value === true ? 'Yes' : 'No';
    }
    if (typeof value === 'number' || !isNaN(parseFloat(value))) {
      return Number(value).toLocaleString('en-US');
    }
    return value?.toString() || '';
  };

  // Get ladder name with fallback to a default value
  const ladderName = ladderData?.nameOfTheLadder || ladderData?.ladderName || "Bond Ladder";
  
  // Get the current rung's maturity range from rungsDetail
  const currentRungMaturity = ladderData?.rungsDetail && 
    ladderData.rungsDetail[selectedRung - 1] ? {
      minMaturity: formatValue('minMaturity', ladderData.rungsDetail[selectedRung - 1].minMaturity),
      maxMaturity: formatValue('maxMaturity', ladderData.rungsDetail[selectedRung - 1].maxMaturity)
    } : null;

  // Pre-filter chips once (exclude maturity-related keys)
  const filteredChips = input
    ? Object.fromEntries(
        Object.entries(input).filter(
          ([key]) => !key.toLowerCase().includes('maturity')
        )
      )
    : null;
  const hasFilteredChips = filteredChips && Object.keys(filteredChips).length > 0;

  return (
    <Box
      sx={{
        color: tokens.colors.fontColour, // replaced "white"
        padding: "16px",
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
      }}
    >
      {/* Container with two vertical boxes */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
      }}>
        {/* Left section - Contains top and bottom boxes */}
        <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          gap: '15px',
          justifyContent: 'center',
        }}>
          {/* Top box - Ladder name and PM/Account info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box 
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                height: '24px',
                position: 'relative',
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontSize: '20px', 
                  fontWeight: 700, 
                  whiteSpace: "nowrap",
                  lineHeight: 1,
                  m: 0,
                  p: 0,
                  color: tokens.colors.fontColour, // replaced 'white'
                  position: 'relative',
                  bottom: '1px',
                }}
              >
                {ladderData?.fromStagingEdit
                  ? `Restaging ${ladderName}`
                  : ladderName}
              </Typography>
            </Box>
            
            {/* PM | Account | Qty | Side | Total Maturity - without pill UI */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              ml: "8px",
              height: '24px',
            }}>
              {/* PM Name */}
              {ladderData?.userName && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      color: tokens.colors.fontColour85, // replaced rgba white
                    }}
                  >
                    Name: {ladderData.userName}
                  </Typography>
                  {/* Fixed vertical separator */}
                  <Box
                    component="span"
                    sx={{
                      height: '18px',
                      width: '2px',
                      backgroundColor: tokens.colors.bodyBorderColor50, // replaced rgba white
                      mx: 1.2,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />
                </>
              )}
              
              {/* Account */}
              {ladderData?.account && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      color: tokens.colors.fontColour85,
                    }}
                  >
                  {ladderData.account}
                  </Typography>
                  {/* Fixed vertical separator */}
                  <Box
                    component="span"
                    sx={{
                      height: '18px',
                      width: '2px',
                      backgroundColor: tokens.colors.bodyBorderColor50,
                      mx: 1.2,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />
                </>
              )}
              
              {/* Side */}
              {ladderData?.side && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      color: tokens.colors.fontColour85,
                    }}
                  >
                    Side: {ladderData.side}
                  </Typography>
                  {/* Fixed vertical separator */}
                  <Box
                    component="span"
                    sx={{
                      height: '18px',
                      width: '2px',
                      backgroundColor: tokens.colors.bodyBorderColor50,
                      mx: 1.2,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />
                </>
              )}
              {/* Quantity */}
              {ladderData?.amountPerRung && (
                <>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 700,
                      whiteSpace: "nowrap",
                      lineHeight: 1.2,
                      color: tokens.colors.fontColour85,
                    }}
                  >
                    Total Amt: {
                      ladderData.qty
                        ? Number(ladderData.qty).toLocaleString('en-US')
                        : (ladderData.totalAmount
                            ? Number(ladderData.totalAmount).toLocaleString('en-US')
                            : ladderData.qty)
                    }
                  </Typography>
                  {/* Fixed vertical separator */}
                  <Box
                    component="span"
                    sx={{
                      height: '18px',
                      width: '2px',
                      backgroundColor: tokens.colors.bodyBorderColor50,
                      mx: 1.2,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                    }}
                  />
                </>
              )}
              {/* Total Maturity */}
              {ladderData?.totalMaturity && (
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    color: tokens.colors.fontColour85,
                  }}
                >
                  Total Maturity: {Number(ladderData.totalMaturity).toLocaleString('en-US')} {Number(ladderData.totalMaturity) === 1 ? 'Month' : 'Months'}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Bottom box - Rung info in pill with other filters */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            minHeight: '28px',
          }}>
            {loading ? (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                backgroundColor: tokens.colors.defaultGridHeaderBackground, // replaced dark bg
                px: 1.5,
                py: 0.5,
                height: '28px',
                borderRadius: '8px',
              }}>
                <CircularProgress size={16} sx={{ mr: 1, color: tokens.palette.primary }} />
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    color: tokens.colors.fontColour90,
                  }}
                >
                  Loading...
                </Typography>
              </Box>
            ) : (
              <Box 
                sx={{ 
                  backgroundColor: tokens.colors.defaultGridHeaderBackground, // replaced dark bg
                  px: 1.5,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  height: '28px',
                  borderRadius: '8px',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                    lineHeight: 1.2,
                    color: tokens.colors.fontColour,
                  }}
                >
                  {`Rung ${selectedRung}${
                    !isRungExecuted && totalElements !== undefined && totalElements !== null
                      ? ` (${Number(totalElements).toLocaleString('en-US')} available)`
                      : ''
                  }`}
                  {currentRungMaturity && (
                    <>
                      <span style={{ margin: '0 6px' }}>|</span>
                      {`Maturity: ${currentRungMaturity.minMaturity} to ${currentRungMaturity.maxMaturity}`}
                    </>
                  )}
                </Typography>
              </Box>
            )}
            
            {/* Show separator only if there are chips to render */}
            {hasFilteredChips && (
              <Box
                component="span"
                sx={{
                  height: '32px',
                  width: '1px',
                  backgroundColor: tokens.colors.bodyBorderColor50,
                  mx: 1,
                  display: 'inline-block',
                  verticalAlign: 'middle'
                }}
              />
            )}
            
            {/* Filter chips (non-maturity only) */}
            {hasFilteredChips && (
              <FilterChips
                input={filteredChips}
                onDeleteChip={onDeleteChip}
                onClearFilters={onClearFilters}
                readOnly={true}
              />
            )}
          </Box>
        </Box>

        {/* Right section - Controls */}
        <Box sx={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 2, 
          flexShrink: 0,
        }}>
          {/* Only show these buttons if NOT coming from staging blotter edit */}
          {!ladderData?.fromStagingEdit && (
            <>
              <Button
                variant="contained"
                color="inherit"
                startIcon={<BorderColor fontSize="small" />}
                sx={{ height: 48, textTransform: "none", borderRadius: "8px" }} // consistent radius
                onClick={handleEditClick}
              >
                Edit Ladder
              </Button>
              <Button
                variant="outlined"
                sx={{ 
                  height: 48, 
                  textTransform: "none", 
                  borderRadius: "8px",
                  bgcolor: tokens.colors.verylightgray, 
                  border: `1px solid ${tokens.palette.danger}`,
                  color: tokens.palette.danger,
                  '&:hover': {
                    bgcolor: tokens.colors.defaultGridHeaderBackground,
                    border: `1px solid ${tokens.palette.danger}`,
                  }
                }}
                onClick={() => setUpdateLadderModalOpen(true)}
              >
                Mark Ladder Not Available
              </Button>
            </>
          )}
          
          <Button
            ref={customizeButtonRef}
            variant="contained"
            color="inherit"
            startIcon={<ViewColumnOutlinedIcon />}
            sx={{ height: 48, textTransform: "none", borderRadius: "8px" }} // consistent radius
            onClick={onCustomizeClick}
          >
            Columns
          </Button>
          <DownloadMenu csvLoading={csvLoading} onSelectFormat={onDownloadCSV} />
         </Box>
       </Box>
       <UpdateLadderStatusModal
         open={updateLadderModalOpen}
         onClose={() => setUpdateLadderModalOpen(false)}
         onUpdateStatus={handleUpdateLadderStatus}
         ladderName={ladderName}
       />
       <EditBondLadder
         open={editModalOpen}
         onClose={() => setEditModalOpen(false)}
         ladderData={ladderData}
         onSubmit={handleEditSubmit}
       />
     </Box>
   );
 };

 const DownloadMenu = ({ csvLoading, onSelectFormat }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  return (
    <>
      <Tooltip title="Download">
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          disabled={csvLoading}
          sx={{
            backgroundColor: tokens.colors.defaultGridRowColor,
            borderRadius: "8px",
            color: tokens.colors.fontColour,
            width: 42,
            height: 42,
            border: `1px solid ${tokens.colors.bodyBorderColor50}`,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          {csvLoading ? <CircularProgress size={24} color="inherit" /> : <DownloadIcon />}
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => { handleClose(); onSelectFormat("csv"); }}>Download CSV</MenuItem>
        <MenuItem onClick={() => { handleClose(); onSelectFormat("xlsx"); }}>Download Excel</MenuItem>
      </Menu>
    </>
  );
};
 
export default BondLadderHeader;